import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import {
  useGetMe,
  useAdminListUsers,
  useAdminCreateUser,
  useAdminUpdateUser,
  useAdminDeleteUser,
  useAdminBulkDeleteUsers,
  useAdminMergeUsers,
  getAdminListUsersQueryKey,
  getGetMeQueryKey,
  type AdminUser,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Plus, Pencil, Trash2, ShieldCheck, UserCog, Search, GitMerge, AlertTriangle, ArrowRight, X } from "lucide-react";
import { toast } from "sonner";
import { capitalizeName } from "@/lib/capitalizeName";
import { apiErrorMessage } from "@/lib/apiError";
import { useDebounce } from "@/hooks/use-debounce";

const PAGE_SIZE = 50;

type CreateForm = {
  emailAddress: string;
  firstName: string;
  lastName: string;
  password: string;
  isAdmin: boolean;
};

const EMPTY_CREATE: CreateForm = {
  emailAddress: "",
  firstName: "",
  lastName: "",
  password: "",
  isAdmin: false,
};

type EditForm = {
  emailAddress: string;
  firstName: string;
  lastName: string;
  isAdmin: boolean;
};

function fullName(u: AdminUser): string {
  const parts = [u.firstName, u.lastName].filter(Boolean) as string[];
  return parts.length > 0 ? capitalizeName(parts.join(" ")) ?? parts.join(" ") : "";
}

function avatarChar(u: AdminUser): string {
  return (
    (u.firstName?.charAt(0) ||
      u.lastName?.charAt(0) ||
      u.email?.charAt(0) ||
      "U").toUpperCase()
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

export default function AdminUsersPage() {
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();

  const meQuery = useGetMe();
  const me = meQuery.data;

  const [searchInput, setSearchInput] = useState("");
  const search = useDebounce(searchInput, 300);
  const [page, setPage] = useState(0);

  // Reset to page 0 whenever the search term changes
  useEffect(() => {
    setPage(0);
  }, [search]);

  // Selection is scoped to the visible page; clear it on page/search change.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  useEffect(() => {
    setSelected(new Set());
  }, [page, search]);

  const listParams = useMemo(
    () => ({
      search: search || undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [search, page],
  );

  const listQuery = useAdminListUsers(listParams, {
    query: {
      queryKey: getAdminListUsersQueryKey(listParams),
      enabled: me?.isAdmin === true,
    },
  });

  // Non-admins (or signed-out users) get bounced back to the dashboard
  // so this page is never visible without authority.
  useEffect(() => {
    if (meQuery.isLoading) return;
    if (!me) return;
    if (!me.isAdmin) navigate("/app");
  }, [me, meQuery.isLoading, navigate]);

  const createUser = useAdminCreateUser();
  const updateUser = useAdminUpdateUser();
  const deleteUser = useAdminDeleteUser();
  const bulkDelete = useAdminBulkDeleteUsers();
  const mergeUsers = useAdminMergeUsers();

  const refetchList = () => {
    queryClient.invalidateQueries({
      queryKey: getAdminListUsersQueryKey().slice(0, 1),
    });
    queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
  };

  const isEligible = (u: AdminUser) =>
    !u.isAdmin && !u.isAdminLockedByEnv && u.clerkUserId !== me?.clerkUserId;

  // ---- Bulk delete ----
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState("");
  const [bulkFailures, setBulkFailures] = useState<
    { clerkUserId: string; email: string; error: string }[]
  >([]);
  const submitBulk = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0 || bulkConfirm !== "DELETE") return;
    const emailOf = (id: string) =>
      listQuery.data?.users.find((u) => u.clerkUserId === id)?.email ?? id;
    try {
      const res = await bulkDelete.mutateAsync({
        data: { clerkUserIds: ids, confirmation: "DELETE" },
      });
      const failed = res.results.filter((r) => !r.deleted);
      const deletedCount = res.results.length - failed.length;
      setBulkFailures(
        failed.map((f) => ({
          clerkUserId: f.clerkUserId,
          email: emailOf(f.clerkUserId),
          error: f.error ?? "Unknown error",
        })),
      );
      setSelected(new Set(failed.map((f) => f.clerkUserId)));
      if (deletedCount > 0) {
        toast.success(`Deleted ${deletedCount} ${deletedCount === 1 ? "user" : "users"}`);
      }
      if (failed.length > 0) {
        toast.error(`${failed.length} could not be deleted`);
      } else {
        setBulkOpen(false);
      }
      setBulkConfirm("");
      // If we just emptied the last page, step back one.
      const remaining = (listQuery.data?.users.length ?? 0) - deletedCount;
      if (remaining <= 0 && page > 0) setPage((p) => Math.max(0, p - 1));
      refetchList();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Bulk delete failed"));
    }
  };

  // ---- Merge ----
  const [mergeSource, setMergeSource] = useState<AdminUser | null>(null);
  const [mergeDest, setMergeDest] = useState("");
  const [mergeConfirm, setMergeConfirm] = useState("");
  const [mergeError, setMergeError] = useState<string | null>(null);
  const [mergeWarning, setMergeWarning] = useState<string | null>(null);
  const openMerge = (u: AdminUser) => {
    setMergeSource(u);
    setMergeDest("");
    setMergeConfirm("");
    setMergeError(null);
    setMergeWarning(null);
  };
  const mergeReady =
    !!mergeSource?.email &&
    mergeDest.trim().length > 3 &&
    mergeDest.trim().toLowerCase() !== mergeSource.email.toLowerCase() &&
    mergeConfirm.trim().toLowerCase() === mergeSource.email.toLowerCase();
  const submitMerge = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mergeSource || !mergeReady) return;
    setMergeError(null);
    try {
      const res = await mergeUsers.mutateAsync({
        clerkUserId: mergeSource.clerkUserId,
        data: {
          destinationEmail: mergeDest.trim(),
          sourceEmailConfirmation: mergeConfirm.trim(),
        },
      });
      refetchList();
      if (res.merged && res.sourceDeleted) {
        toast.success(res.message || "Accounts merged");
        setMergeSource(null);
      } else {
        setMergeWarning(
          res.message ||
            "Data was transferred, but the source login could not be removed. Retry the merge to finish cleanup.",
        );
      }
    } catch (err) {
      const status = (err as { status?: number })?.status;
      setMergeError(
        (status === 409 ? "Conflict: " : "") + apiErrorMessage(err, "Could not merge accounts"),
      );
      refetchList();
    }
  };

  // ---- Create dialog state ----
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateForm>(EMPTY_CREATE);
  const submitCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createUser.mutateAsync({
        data: {
          emailAddress: createForm.emailAddress.trim(),
          firstName: createForm.firstName.trim() || null,
          lastName: createForm.lastName.trim() || null,
          password: createForm.password.trim() || null,
          isAdmin: createForm.isAdmin,
        },
      });
      toast.success("User created");
      setCreateOpen(false);
      setCreateForm(EMPTY_CREATE);
      refetchList();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not create user"));
    }
  };

  // ---- Edit dialog state ----
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const emailLocked = (u: AdminUser) =>
    u.isAdminLockedByEnv || u.clerkUserId === me?.clerkUserId;
  const [editForm, setEditForm] = useState<EditForm>({
    emailAddress: "",
    firstName: "",
    lastName: "",
    isAdmin: false,
  });
  const openEdit = (u: AdminUser) => {
    setEditing(u);
    setEditForm({
      emailAddress: u.email ?? "",
      firstName: u.firstName ?? "",
      lastName: u.lastName ?? "",
      isAdmin: u.isAdmin,
    });
  };
  const submitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    const nextEmail = editForm.emailAddress.trim();
    const emailChanged =
      !!nextEmail && nextEmail.toLowerCase() !== (editing.email ?? "").toLowerCase();
    try {
      await updateUser.mutateAsync({
        clerkUserId: editing.clerkUserId,
        data: {
          ...(emailChanged && !emailLocked(editing) ? { emailAddress: nextEmail } : {}),
          firstName: editForm.firstName.trim() || null,
          lastName: editForm.lastName.trim() || null,
          // If env locks them as admin we don't even send the bit.
          isAdmin: editing.isAdminLockedByEnv ? undefined : editForm.isAdmin,
        },
      });
      toast.success("User updated");
      setEditing(null);
      refetchList();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not update user"));
    }
  };

  // ---- Delete confirm state ----
  const [confirmDelete, setConfirmDelete] = useState<AdminUser | null>(null);
  const submitDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteUser.mutateAsync({ clerkUserId: confirmDelete.clerkUserId });
      toast.success("User deleted");
      setConfirmDelete(null);
      refetchList();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not delete user"));
    }
  };

  // ---- Inline admin toggle ----
  const toggleAdmin = async (u: AdminUser, next: boolean) => {
    if (u.isAdminLockedByEnv) return;
    try {
      await updateUser.mutateAsync({
        clerkUserId: u.clerkUserId,
        data: { isAdmin: next },
      });
      toast.success(next ? "Granted admin" : "Revoked admin");
      refetchList();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not change admin status"));
    }
  };

  if (meQuery.isLoading || (me && !me.isAdmin)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const users = listQuery.data?.users ?? [];
  const totalCount = listQuery.data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const eligibleOnPage = users.filter(isEligible);
  const allSelected =
    eligibleOnPage.length > 0 && eligibleOnPage.every((u) => selected.has(u.clerkUserId));
  const someSelected = eligibleOnPage.some((u) => selected.has(u.clerkUserId));
  const toggleAll = (on: boolean) =>
    setSelected(on ? new Set(eligibleOnPage.map((u) => u.clerkUserId)) : new Set());
  const toggleOne = (id: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  const selectedUsers = users.filter((u) => selected.has(u.clerkUserId));

  return (
    <div className="px-6 py-8 max-w-7xl mx-auto w-full">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground mb-2">
            <UserCog className="w-3.5 h-3.5" />
            Admin
          </div>
          <h1 className="text-2xl font-semibold">Users</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Add, modify, or remove anyone who can sign in to MailCraft.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="button-add-user">
          <Plus className="w-4 h-4 mr-2" />
          Add user
        </Button>
      </div>

      <Card>
        <CardHeader className="border-b py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="text-base font-medium">
              {totalCount} {totalCount === 1 ? "user" : "users"}
            </CardTitle>
            <div className="relative w-72 max-w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by email or name"
                className="pl-9"
                data-testid="input-search-users"
              />
            </div>
          </div>
          {selected.size > 0 && (
            <div
              className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2"
              data-testid="bar-bulk-selection"
            >
              <span className="text-sm" data-testid="text-selected-count">
                {selected.size} selected on this page
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelected(new Set())}
                  disabled={bulkDelete.isPending}
                  data-testid="button-clear-selection"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    setBulkConfirm("");
                    setBulkFailures([]);
                    setBulkOpen(true);
                  }}
                  disabled={bulkDelete.isPending}
                  data-testid="button-bulk-delete"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete selected
                </Button>
              </div>
            </div>
          )}
          {bulkFailures.length > 0 && !bulkOpen && (
            <div
              role="alert"
              className="mt-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm"
              data-testid="alert-bulk-failures"
            >
              <div className="font-medium text-destructive mb-1">
                {bulkFailures.length} could not be deleted (still selected)
              </div>
              <ul className="space-y-0.5">
                {bulkFailures.map((f) => (
                  <li key={f.clerkUserId} className="text-muted-foreground">
                    <span className="text-foreground">{f.email}</span>: {f.error}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {listQuery.isLoading ? (
            <div className="py-16 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : listQuery.isError ? (
            <div className="py-16 text-center text-sm text-destructive">
              {apiErrorMessage(listQuery.error, "Failed to load users")}
            </div>
          ) : users.length === 0 ? (
            <div className="py-16 text-center text-sm text-muted-foreground">
              {search ? "No users match that search." : "No users yet."}
            </div>
          ) : (
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={allSelected ? true : someSelected ? "indeterminate" : false}
                      onCheckedChange={(v) => toggleAll(v === true)}
                      disabled={eligibleOnPage.length === 0 || bulkDelete.isPending}
                      aria-label="Select all eligible users on this page"
                      data-testid="checkbox-select-all"
                    />
                  </TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className="w-32">Admin</TableHead>
                  <TableHead className="w-32">Joined</TableHead>
                  <TableHead className="w-32">Last sign-in</TableHead>
                  <TableHead className="w-32 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => {
                  const name = fullName(u);
                  const isMe = u.clerkUserId === me?.clerkUserId;
                  return (
                    <TableRow key={u.clerkUserId} data-testid={`row-user-${u.clerkUserId}`}>
                      <TableCell>
                        <Checkbox
                          checked={selected.has(u.clerkUserId)}
                          onCheckedChange={(v) => toggleOne(u.clerkUserId, v === true)}
                          disabled={!isEligible(u) || bulkDelete.isPending}
                          aria-label={
                            isEligible(u)
                              ? `Select ${u.email ?? u.clerkUserId}`
                              : `${u.email ?? u.clerkUserId} cannot be bulk deleted`
                          }
                          title={isEligible(u) ? undefined : "Admins and your own account are excluded"}
                          data-testid={`checkbox-user-${u.clerkUserId}`}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            {u.imageUrl ? (
                              <AvatarImage src={u.imageUrl} alt={name || u.email || ""} />
                            ) : null}
                            <AvatarFallback>{avatarChar(u)}</AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <div className="font-medium truncate flex items-center gap-2">
                              {name || <span className="text-muted-foreground italic">No name</span>}
                              {isMe && (
                                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                                  You
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        <span className="truncate block max-w-[18rem]">{u.email ?? "—"}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={u.isAdmin}
                            onCheckedChange={(next) => toggleAdmin(u, next)}
                            disabled={
                              u.isAdminLockedByEnv ||
                              isMe ||
                              updateUser.isPending
                            }
                            aria-label={`Toggle admin for ${u.email ?? u.clerkUserId}`}
                            data-testid={`switch-admin-${u.clerkUserId}`}
                          />
                          {u.isAdminLockedByEnv && (
                            <Badge
                              variant="outline"
                              className="text-[10px] gap-1"
                              title="Locked by MAILCRAFT_ADMIN_EMAILS env var"
                            >
                              <ShieldCheck className="w-3 h-3" />
                              Env
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {formatDate(u.createdAt)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {formatDate(u.lastSignInAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8"
                            onClick={() => openEdit(u)}
                            aria-label="Edit user"
                            data-testid={`button-edit-user-${u.clerkUserId}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8"
                            onClick={() => openMerge(u)}
                            disabled={isMe || u.isAdminLockedByEnv || !u.email || mergeUsers.isPending}
                            aria-label="Merge into another account"
                            title="Merge into another account"
                            data-testid={`button-merge-user-${u.clerkUserId}`}
                          >
                            <GitMerge className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => setConfirmDelete(u)}
                            disabled={isMe || u.isAdmin || u.isAdminLockedByEnv || deleteUser.isPending}
                            aria-label="Delete user"
                            data-testid={`button-delete-user-${u.clerkUserId}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm">
          <span className="text-muted-foreground">
            Page {page + 1} of {totalPages}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => p + 1)}
              disabled={page + 1 >= totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* ---- Add user dialog ---- */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add user</DialogTitle>
            <DialogDescription>
              Creates a new MailCraft account. If you set a password they can
              sign in with it after verifying their email; otherwise MailCraft
              emails them a link to choose their own password.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitCreate} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="new-first">First name</Label>
                <Input
                  id="new-first"
                  value={createForm.firstName}
                  onChange={(e) => setCreateForm((f) => ({ ...f, firstName: e.target.value }))}
                  data-testid="input-new-first-name"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="new-last">Last name</Label>
                <Input
                  id="new-last"
                  value={createForm.lastName}
                  onChange={(e) => setCreateForm((f) => ({ ...f, lastName: e.target.value }))}
                  data-testid="input-new-last-name"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-email">Email</Label>
              <Input
                id="new-email"
                type="email"
                value={createForm.emailAddress}
                onChange={(e) => setCreateForm((f) => ({ ...f, emailAddress: e.target.value }))}
                placeholder="user@company.com"
                required
                autoFocus
                data-testid="input-new-email"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-password">Password (optional)</Label>
              <Input
                id="new-password"
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))}
                placeholder="At least 8 characters, or leave blank"
                minLength={8}
                data-testid="input-new-password"
              />
              <p className="text-xs text-muted-foreground">
                Leave blank to let them set one via password reset on first sign-in.
              </p>
            </div>
            <div className="flex items-center justify-between rounded-md border px-3 py-2">
              <div>
                <div className="text-sm font-medium">Make this user an admin</div>
                <div className="text-xs text-muted-foreground">
                  Admins can manage every user, template, and setting.
                </div>
              </div>
              <Switch
                checked={createForm.isAdmin}
                onCheckedChange={(v) => setCreateForm((f) => ({ ...f, isAdmin: v }))}
                data-testid="switch-new-admin"
              />
            </div>
            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setCreateOpen(false)}
                disabled={createUser.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createUser.isPending}>
                {createUser.isPending && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}
                Create user
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ---- Edit user dialog ---- */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit user</DialogTitle>
            <DialogDescription>
              {editing?.email ?? "User"}
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <form onSubmit={submitEdit} className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editForm.emailAddress}
                  onChange={(e) => setEditForm((f) => ({ ...f, emailAddress: e.target.value }))}
                  disabled={emailLocked(editing)}
                  aria-describedby="edit-email-help"
                  data-testid="input-edit-email"
                />
                <p id="edit-email-help" className="text-xs text-muted-foreground">
                  {emailLocked(editing)
                    ? editing.clerkUserId === me?.clerkUserId
                      ? "You cannot change your own email here, to avoid locking yourself out."
                      : "This email is listed in MAILCRAFT_ADMIN_EMAILS and cannot be changed here."
                    : "Changes the sign-in email and keeps all of this user's data. The new address must not belong to another account; use Merge for that."}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="edit-first">First name</Label>
                  <Input
                    id="edit-first"
                    value={editForm.firstName}
                    onChange={(e) => setEditForm((f) => ({ ...f, firstName: e.target.value }))}
                    data-testid="input-edit-first-name"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit-last">Last name</Label>
                  <Input
                    id="edit-last"
                    value={editForm.lastName}
                    onChange={(e) => setEditForm((f) => ({ ...f, lastName: e.target.value }))}
                    data-testid="input-edit-last-name"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between rounded-md border px-3 py-2">
                <div>
                  <div className="text-sm font-medium">Admin</div>
                  <div className="text-xs text-muted-foreground">
                    {editing.isAdminLockedByEnv
                      ? "Locked on by MAILCRAFT_ADMIN_EMAILS — change the env var to revoke."
                      : "Toggle to grant or revoke admin powers."}
                  </div>
                </div>
                <Switch
                  checked={editForm.isAdmin}
                  onCheckedChange={(v) => setEditForm((f) => ({ ...f, isAdmin: v }))}
                  disabled={editing.isAdminLockedByEnv}
                  data-testid="switch-edit-admin"
                />
              </div>
              <DialogFooter className="pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setEditing(null)}
                  disabled={updateUser.isPending}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={updateUser.isPending}>
                  {updateUser.isPending && (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  )}
                  Save changes
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* ---- Bulk delete dialog ---- */}
      <Dialog
        open={bulkOpen}
        onOpenChange={(o) => {
          if (bulkDelete.isPending) return;
          setBulkOpen(o);
        }}
      >
        <DialogContent className="sm:max-w-lg max-h-[90dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Delete {selectedUsers.length} {selectedUsers.length === 1 ? "user" : "users"}?</DialogTitle>
            <DialogDescription>
              Each account is removed from MailCraft along with everything it owns:
              templates, contacts, brand settings, send logs. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <ul
            className="max-h-48 overflow-y-auto rounded-md border divide-y text-sm"
            data-testid="list-bulk-emails"
          >
            {selectedUsers.map((u) => {
              const fail = bulkFailures.find((f) => f.clerkUserId === u.clerkUserId);
              return (
                <li key={u.clerkUserId} className="px-3 py-1.5">
                  <div className="truncate">{u.email ?? u.clerkUserId}</div>
                  {fail && (
                    <div className="text-xs text-destructive" data-testid={`text-bulk-error-${u.clerkUserId}`}>
                      Failed: {fail.error}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          <div className="space-y-1.5">
            <Label htmlFor="bulk-confirm">
              Type <span className="font-mono font-semibold">DELETE</span> to confirm
            </Label>
            <Input
              id="bulk-confirm"
              value={bulkConfirm}
              onChange={(e) => setBulkConfirm(e.target.value)}
              autoComplete="off"
              disabled={bulkDelete.isPending}
              data-testid="input-bulk-confirm"
            />
          </div>
          <DialogFooter className="pt-2 gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setBulkOpen(false)}
              disabled={bulkDelete.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={submitBulk}
              disabled={bulkConfirm !== "DELETE" || selectedUsers.length === 0 || bulkDelete.isPending}
              data-testid="button-confirm-bulk-delete"
            >
              {bulkDelete.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete {selectedUsers.length}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Merge dialog ---- */}
      <Dialog
        open={!!mergeSource}
        onOpenChange={(o) => {
          if (mergeUsers.isPending) return;
          if (!o) setMergeSource(null);
        }}
      >
        <DialogContent className="sm:max-w-lg max-h-[90dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Merge accounts</DialogTitle>
            <DialogDescription>
              Moves the source account's data into the destination account, then
              removes the source login. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {mergeSource && (
            <form onSubmit={submitMerge} className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] items-center gap-2 text-sm">
                <div className="rounded-md border px-3 py-2 min-w-0">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Source (removed)</div>
                  <div className="truncate font-medium" data-testid="text-merge-source">{mergeSource.email}</div>
                </div>
                <ArrowRight className="w-4 h-4 mx-auto text-muted-foreground rotate-90 sm:rotate-0" aria-hidden />
                <div className="rounded-md border px-3 py-2 min-w-0">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Destination (kept)</div>
                  <div className="truncate font-medium">{mergeDest.trim() || "Enter below"}</div>
                </div>
              </div>
              <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-xs space-y-1">
                <div className="flex items-center gap-1.5 font-medium text-sm">
                  <AlertTriangle className="w-4 h-4" /> Before you merge
                </div>
                <ul className="list-disc pl-5 space-y-0.5 text-muted-foreground">
                  <li>Destination settings and preferences win; source settings are discarded.</li>
                  <li>Source API keys and its Gmail connection are revoked.</li>
                  <li>Duplicate template or snippet slugs, or conflicting drafts, block the merge. Nothing is lost; resolve them and retry.</li>
                  <li>The source account is disabled while the merge runs.</li>
                </ul>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="merge-dest">Destination email</Label>
                <Input
                  id="merge-dest"
                  type="email"
                  value={mergeDest}
                  onChange={(e) => setMergeDest(e.target.value)}
                  placeholder="existing.account@company.com"
                  required
                  disabled={mergeUsers.isPending}
                  data-testid="input-merge-destination"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="merge-confirm">
                  Type the source email <span className="font-mono">{mergeSource.email}</span> to confirm
                </Label>
                <Input
                  id="merge-confirm"
                  value={mergeConfirm}
                  onChange={(e) => setMergeConfirm(e.target.value)}
                  autoComplete="off"
                  disabled={mergeUsers.isPending}
                  data-testid="input-merge-confirm"
                />
              </div>
              {mergeError && (
                <div role="alert" className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive" data-testid="alert-merge-error">
                  {mergeError}
                </div>
              )}
              {mergeWarning && (
                <div role="alert" className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-sm" data-testid="alert-merge-warning">
                  {mergeWarning} Transferred data is retained in the destination.
                </div>
              )}
              <DialogFooter className="pt-2 gap-2">
                <Button type="button" variant="ghost" onClick={() => setMergeSource(null)} disabled={mergeUsers.isPending}>
                  {mergeWarning ? "Close" : "Cancel"}
                </Button>
                <Button
                  type="submit"
                  variant="destructive"
                  disabled={!mergeReady || mergeUsers.isPending}
                  data-testid="button-confirm-merge"
                >
                  {mergeUsers.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {mergeWarning ? "Retry merge" : "Merge accounts"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* ---- Delete confirm ---- */}
      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this user?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes <span className="font-medium">
                {confirmDelete?.email ?? confirmDelete?.clerkUserId}
              </span>{" "}
              from MailCraft and wipes everything they own — templates,
              contacts, brand settings, send logs. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteUser.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                submitDelete();
              }}
              disabled={deleteUser.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteUser.isPending && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              Delete user
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
